// ──────────────────────────────────────────────────────────────────────
// Batch 5 R4 F3 — Velocity Engine Settings panel.
// Workspace-wide view (Owner/Admin only — gated upstream in SettingsPage).
// Shows team-wide average min/task by deliverable_type + per-user
// breakdown, plus on-time delivery rate.
// ──────────────────────────────────────────────────────────────────────
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { velocityAPI } from '../lib/api';
import { Gauge, Loader2, Activity, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';

function fmtMin(m) {
  if (m == null) return '—';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const rem = Math.round(m % 60);
  return rem === 0 ? `${h}h` : `${h}h ${rem}m`;
}

export default function VelocitySettingsPanel({ workspaceId }) {
  const [loading, setLoading] = useState(true);
  const [team, setTeam] = useState(null);
  const [onTime, setOnTime] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!workspaceId) return;
    let abort = false;
    setLoading(true);
    setErr(null);
    Promise.all([
      velocityAPI.team(workspaceId),
      velocityAPI.onTimeRate(workspaceId),
    ])
      .then(([t, o]) => {
        if (abort) return;
        setTeam(t.data);
        setOnTime(o.data);
      })
      .catch((e) => { if (!abort) setErr(e?.response?.data?.detail || 'Failed to load velocity data'); })
      .finally(() => { if (!abort) setLoading(false); });
    return () => { abort = true; };
  }, [workspaceId]);

  if (loading) {
    return <div className="text-center py-12 text-[12px] text-muted-foreground/50" data-testid="velocity-loading"><Loader2 className="h-4 w-4 animate-spin inline mr-1.5" /> Loading velocity data…</div>;
  }
  if (err) {
    return (
      <Card className="bg-card border-amber-500/30" data-testid="velocity-error">
        <CardContent className="p-4 text-center">
          <AlertTriangle className="h-5 w-5 text-amber-400 mx-auto mb-2" />
          <p className="text-[12px] text-amber-200/80">{err}</p>
        </CardContent>
      </Card>
    );
  }

  const types = (team?.by_deliverable_type) || [];
  const users = (team?.by_user) || [];
  const totalBatches = team?.total_batches || 0;

  return (
    <div className="space-y-4" data-testid="velocity-settings-panel">
      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card className="bg-card border-border/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground/40">Total batches</span>
              <Gauge className="h-3.5 w-3.5 text-muted-foreground/30" />
            </div>
            <div className="text-[24px] font-display font-semibold text-gold" data-testid="velocity-total-batches">{totalBatches}</div>
            <div className="text-[10px] text-muted-foreground/40 mt-0.5">Auto + manual rollups</div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground/40">On-time rate</span>
              <Activity className="h-3.5 w-3.5 text-muted-foreground/30" />
            </div>
            <div className="text-[24px] font-display font-semibold text-emerald-300" data-testid="velocity-on-time-rate">
              {onTime?.on_time_rate_pct != null ? `${onTime.on_time_rate_pct}%` : '—'}
            </div>
            <div className="text-[10px] text-muted-foreground/40 mt-0.5">
              {onTime?.on_time ?? 0} on-time · {onTime?.late ?? 0} late
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground/40">Top deliverable</span>
              <Gauge className="h-3.5 w-3.5 text-muted-foreground/30" />
            </div>
            <div className="text-[18px] font-display font-semibold text-cyan-300" data-testid="velocity-top-deliverable">
              {types[0]?.deliverable_type || '—'}
            </div>
            <div className="text-[10px] text-muted-foreground/40 mt-0.5">
              {types[0] ? `${fmtMin(types[0].avg_minutes_per_task)} avg/task` : 'No data yet'}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* By deliverable type */}
      <Card className="bg-card/60 backdrop-blur">
        <CardHeader className="pb-2">
          <CardTitle className="text-[13px] font-semibold flex items-center gap-2">
            <Gauge className="h-3.5 w-3.5 text-gold" /> Team velocity by deliverable type
          </CardTitle>
        </CardHeader>
        <CardContent>
          {types.length === 0 ? (
            <p className="text-[12px] text-muted-foreground/50 text-center py-6" data-testid="velocity-types-empty">
              No velocity batches yet. They appear automatically when a teammate completes 2 or more tasks of the same deliverable_type for the same client during one clock-in session.
            </p>
          ) : (
            <table className="w-full text-[12px]" data-testid="velocity-types-table">
              <thead>
                <tr className="text-muted-foreground/50 border-b border-border/20">
                  <th className="text-left py-1.5 px-2 font-medium">Deliverable type</th>
                  <th className="text-right py-1.5 px-2 font-medium">Tasks</th>
                  <th className="text-right py-1.5 px-2 font-medium">Total time</th>
                  <th className="text-right py-1.5 px-2 font-medium">Avg / task</th>
                </tr>
              </thead>
              <tbody>
                {types.map((row) => (
                  <tr key={row.deliverable_type} className="border-b border-border/10 last:border-0">
                    <td className="py-1.5 px-2 font-medium text-foreground">{row.deliverable_type}</td>
                    <td className="py-1.5 px-2 text-right text-muted-foreground/70">{row.task_count}</td>
                    <td className="py-1.5 px-2 text-right text-muted-foreground/70">{fmtMin(row.total_minutes)}</td>
                    <td className="py-1.5 px-2 text-right font-medium text-gold">{fmtMin(row.avg_minutes_per_task)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* By user */}
      <Card className="bg-card/60 backdrop-blur">
        <CardHeader className="pb-2">
          <CardTitle className="text-[13px] font-semibold flex items-center gap-2">
            <Activity className="h-3.5 w-3.5 text-gold" /> Per-user breakdown
          </CardTitle>
        </CardHeader>
        <CardContent>
          {users.length === 0 ? (
            <p className="text-[12px] text-muted-foreground/50 text-center py-6" data-testid="velocity-users-empty">No user-level batches yet.</p>
          ) : (
            <table className="w-full text-[12px]" data-testid="velocity-users-table">
              <thead>
                <tr className="text-muted-foreground/50 border-b border-border/20">
                  <th className="text-left py-1.5 px-2 font-medium">Member</th>
                  <th className="text-right py-1.5 px-2 font-medium">Tasks</th>
                  <th className="text-right py-1.5 px-2 font-medium">Total time</th>
                  <th className="text-right py-1.5 px-2 font-medium">Avg / task</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.user_id} className="border-b border-border/10 last:border-0">
                    <td className="py-1.5 px-2 font-medium text-foreground">
                      <Link to={`/people/${u.user_id}`} className="hover:text-gold transition-colors" data-testid={`velocity-user-${u.user_id}`}>
                        {u.name}
                      </Link>
                    </td>
                    <td className="py-1.5 px-2 text-right text-muted-foreground/70">{u.task_count}</td>
                    <td className="py-1.5 px-2 text-right text-muted-foreground/70">{fmtMin(u.total_minutes)}</td>
                    <td className="py-1.5 px-2 text-right font-medium text-gold">{fmtMin(u.avg_minutes_per_task)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
